import { Component, h, Prop, State, Element } from '@stencil/core';

/**
 * @category Hero headers (Intro)
 * @slot heading - {required}, <me-heading variant="1"> - Heading slot for main title
 * @slot description - {required}, <me-text tag="p" variant="large"> - Description slot for subtitle or lead text
 * @slot media - {required}, <me-video-youtube lazy="false">, <me-picture lazy="false">, <me-video lazy="false"> - Media slot for video or image
 * @slot action-primary - {required}, <me-button variant="primary">, <form-subscribe-type-1> - Primary action slot
 * @slot action-secondary - <me-button variant="secondary"> - Secondary action slot
 * @example
 *   <hero-header-type-1>
 *     <me-heading slot="heading" variant="1">Simplify complexity: optimize, scale, excel</me-heading>
 *     <me-text slot="description" tag="p" variant="large">Upgrade your productivity with tools crafted to simplify challenges and fuel growth. Refine processes, scale smoothly, and accomplish your goals faster with our advanced solution.</me-text>
 *     <me-picture slot="media" src="https://images.unsplash.com/photo-1504548840739-580b10ae7715?q=80&w=3000&auto=format&fit=crop" lazy="false" alt="Example image"></me-picture>
 *     <me-button slot="action-primary" variant="primary">Get started</me-button>
 *     <me-button slot="action-secondary" variant="secondary">Contact Sales</me-button>
 *   </hero-header-type-1>
 */
@Component({
  tag: 'hero-header-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class HeroHeaderType1 {
  @Element() hostEl!: HTMLElement;

  /**
   * Position of the content block: 'left' or 'right'.
   * If 'left', content is on the left and media is on the right.
   * If 'right', content is on the right and media is on the left.
   * @default left
   */
  @Prop() contentPosition: 'left' | 'right' = 'left';

  @State() hasSecondaryAction: boolean = false;

  componentWillLoad() {
    this.checkSecondaryAction();
  }

  componentDidLoad() {
    this.checkSecondaryAction();
  }

  private checkSecondaryAction() {
    const secondaryActionElements = this.hostEl.querySelectorAll('[slot="action-secondary"]');
    this.hasSecondaryAction = secondaryActionElements.length > 0;
  }

  render() {
    return (
      <me-container yGap="2xl" yGapLg="4xl">
        <me-row xGapLg="3xl" yGap="md" yGapLg="4xl" align="center" justify="center" direction={this.contentPosition === 'right' ? 'row-reverse' : 'row'}>
          <me-column spanLg="6" span="12">
            <slot name="heading"></slot>
            <slot name="description"></slot>
            <me-row xGap="md" xGapLg="md" yGap="none" yGapLg="md" class="hero-header-type-1__actions-group">
              <me-column span="auto">
                <slot name="action-primary"></slot>
              </me-column>
              {this.hasSecondaryAction && (
                <me-column span="auto">
                  <slot name="action-secondary"></slot>
                </me-column>
              )}
            </me-row>
          </me-column>
          <me-column spanLg="6" span="12">
            <slot name="media"></slot>
          </me-column>
        </me-row>
      </me-container>
    );
  }
}
